import { useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import { toast } from "react-toastify"
import withRouter from "Components/Common/withRouter"
import useManual from "hooks/useManual"
import Client from "models/client"
import Opportunity, { IOpportunityQueryParam } from "models/opportunity"
import { ProjectListItem, IProjectQueryParam } from "models/project"
import { ClientEngagement, IClientEngagementQueryParams } from "models/client-engagement"
import { getClient } from "slices/business-development/client/thunk"
import { getOpportunities } from "slices/business-development/opportunities/thunk"
import { getProjects } from "slices/thunk"
import { getClientEngagements, getClientEngagementSearchFields } from "slices/thunk"
import { getOpportunitySearchFields } from "slices/thunk"
import { getProjectSearchFields } from "slices/thunk"
import { OpportunityForecastSliceState } from "slices/business-development/opportunity-forecast/reducer"
import { ProjectSliceState } from "slices/project-managment/projects/reducer"
import { ClientEngagementSliceState, setClientEngagementSearchFields } from "slices/business-development/client-engagement/reducer"
import DataTable from "Components/molecule/data-table"
import ClientDetailsTemplate from "Components/templates/business-development/client-details"
import { formatCurrency } from "utils/tools"
import { PAGE_KEY, LIMIT_KEY } from "models/queryable"
import LoadingState from "Components/organisms/loading-state"

const SECTION_OPPORTUNITIES = "Opportunities"
const SECTION_PROJECTS = "Projects"
const SECTION_CLIENT_ENGAGEMENTS = "Client Engagements"
// The sub-tables load every row; sorting is done client-side by clicking a column title
const ALL_ROWS = Number.MAX_SAFE_INTEGER

const selectOpportunitySearchFields = createSelector(
    (state: any) => state.OpportunityForecast,
    ({ branches, projectManagers, departments, searchFieldsLoaded }: OpportunityForecastSliceState) => ({
        branches, projectManagers, departments, searchFieldsLoaded
    })
)

const selectProjectSearchFields = createSelector(
    (state: any) => state.Projects,
    ({ projectStatus, departments }: ProjectSliceState) => ({
        projectStatus, departments
    })
)

const selectClientEngagementSearchFields = createSelector(
    (state: any) => state.ClientEngagement,
    ({ employees, opportunities, clients }: ClientEngagementSliceState) => ({
        employees, opportunities, clients
    })
)

const ClientDetailsPage = (props: any) => {
    document.title = "Client | IbexIQ"
    const dispatch: any = useDispatch()
    const [searchParams] = useSearchParams()
    const { handleOpenManual } = useManual("business-development/client");

    const clientId = useMemo(() => searchParams.get("clientId"), [searchParams])

    const [client, setClient] = useState<Client | null>(null)
    const [isLoadingClient, setIsLoadingClient] = useState<boolean>(true)

    // Opportunities section
    const [opportunities, setOpportunities] = useState<Opportunity[]>([])
    const [isLoadingOpportunities, setIsLoadingOpportunities] = useState<boolean>(true)
    const [opportunityQuery, setOpportunityQuery] = useState<IOpportunityQueryParam>({
        page: 0,
        limit: ALL_ROWS,
        branchIds: [],
        ownerIds: [],
        departmentIds: [],
        clientIds: clientId ? [clientId] : [],
    })

    // Projects section
    const [projects, setProjects] = useState<ProjectListItem[]>([])
    const [isLoadingProjects, setIsLoadingProjects] = useState<boolean>(true)
    const [projectQuery, setProjectQuery] = useState<IProjectQueryParam>({
        page: 0,
        limit: ALL_ROWS,
        clientIds: clientId ? [clientId] : [],
        branchIds: [],
        projectManagerIds: [],
        departmentIds: [],
        statusIds: [],
    })

    // Client Engagements section
    const [clientEngagements, setClientEngagements] = useState<ClientEngagement[]>([])
    const [isLoadingClientEngagements, setIsLoadingClientEngagements] = useState<boolean>(true)
    const [clientEngagementQuery, setClientEngagementQuery] = useState<IClientEngagementQueryParams>({
        page: 0,
        limit: ALL_ROWS,
        opportunityIds: [],
        createdByIds: [],
        clientIds: clientId ? [clientId] : [],
    })

    const {
        branches: opportunityBranches,
        projectManagers: opportunityProjectManagers,
        departments: opportunityDepartments,
        searchFieldsLoaded: opportunitySearchFieldsLoaded,
    } = useSelector(selectOpportunitySearchFields)

    const {
        projectStatus,
        departments,
    } = useSelector(selectProjectSearchFields)

    const {
        employees: clientEngagementEmployees,
        opportunities: clientEngagementOpportunities,
        clients: clientEngagementClients,
    } = useSelector(selectClientEngagementSearchFields)

    const queryOpportunities = useCallback(async (q: IOpportunityQueryParam) => {
        setIsLoadingOpportunities(true)
        const res = await getOpportunities(q)
        if (res) {
            setOpportunities(res.data)
        }
        setIsLoadingOpportunities(false)
    }, [])

    const queryProjects = useCallback(async (q: IProjectQueryParam) => {
        setIsLoadingProjects(true)
        const res = await getProjects(q)
        if (res) {
            setProjects(res.data)
        }
        setIsLoadingProjects(false)
    }, [])

    const queryClientEngagements = useCallback(async (q: IClientEngagementQueryParams) => {
        setIsLoadingClientEngagements(true)
        const res = await getClientEngagements(q)
        if (res) {
            setClientEngagements(res.data)
        }
        setIsLoadingClientEngagements(false)
    }, [])

    const initializePage = async (id: string) => {
        const loadedClient = await getClient(id)

        if (!loadedClient) {
            toast.error("Could not load client")
            props.router.navigate(-1)
            return
        }

        setClient(loadedClient)
        setIsLoadingClient(false)

        if (!opportunitySearchFieldsLoaded) {
            await dispatch(getOpportunitySearchFields())
        }

        if (projectStatus.length === 0 || departments.length === 0) {
            await dispatch(getProjectSearchFields())
        }

        if (clientEngagementEmployees.length === 0 || clientEngagementOpportunities.length === 0 || clientEngagementClients.length === 0) {
            const res = await getClientEngagementSearchFields()
            if (res) dispatch(setClientEngagementSearchFields(res))
        }

        await Promise.all([
            queryOpportunities(opportunityQuery),
            queryProjects(projectQuery),
            queryClientEngagements(clientEngagementQuery),
        ])
    }

    useEffect(() => {
        if (!clientId) {
            toast.error("Client Id not found")
            props.router.navigate(-1)
            return
        }
        initializePage(clientId)
    }, [])

    const handleClickProject = (projectId: string) => {
        const url = `/project-managment/projects/project?projectId=${projectId}`
        window.open(url, "_blank", "noopener,noreferrer")
    }

    const handleClickOpportunity = (project: Opportunity) => {
        if (!process.env.REACT_APP_ACUMATICA_PROJECT_URL) {
            toast.error("Could not find Acumatica URL.")
            return
        }

        const url = process.env.REACT_APP_ACUMATICA_OPPORTUNITY_URL + encodeURIComponent(project.id)
        window.open(url, "_blank")
    }


    // ---- Opportunities section handlers ----
    const handleChangeOpportunityQuery = (key: string, value: any) => {
        const q = { ...opportunityQuery }
        if (key !== PAGE_KEY && key !== LIMIT_KEY) {
            q.page = 0
            q.limit = ALL_ROWS
        }
        q[key] = value
        setOpportunityQuery(q)
    }

    const handleSearchOpportunities = useCallback(() => queryOpportunities(opportunityQuery), [opportunityQuery, queryOpportunities])

    const handleClearOpportunitySearch = useCallback(() => {
        const q: IOpportunityQueryParam = {
            page: 0,
            limit: ALL_ROWS,
            branchIds: [],
            ownerIds: [],
            departmentIds: [],
            clientIds: clientId ? [clientId] : [],
        }
        setOpportunityQuery(q)
        queryOpportunities(q)
    }, [clientId, queryOpportunities])

    // ---- Projects section handlers ----
    const handleChangeProjectQuery = (key: string, value: any) => {
        const q = { ...projectQuery }
        if (key !== PAGE_KEY && key !== LIMIT_KEY) {
            q.page = 0
            q.limit = ALL_ROWS
        }
        q[key] = value
        setProjectQuery(q)
    }

    const handleSearchProjects = useCallback(() => queryProjects(projectQuery), [projectQuery, queryProjects])

    const handleClearProjectSearch = useCallback(() => {
        const q: IProjectQueryParam = {
            page: 0,
            limit: ALL_ROWS,
            clientIds: clientId ? [clientId] : [],
            branchIds: [],
            projectManagerIds: [],
            departmentIds: [],
            statusIds: [],
        }
        setProjectQuery(q)
        queryProjects(q)
    }, [clientId, queryProjects])

    // ---- Client Engagements section handlers ----
    const handleChangeClientEngagementQuery = (key: string, value: any) => {
        const q: any = { ...clientEngagementQuery }
        if (key !== PAGE_KEY && key !== LIMIT_KEY) {
            q.page = 0
            q.limit = ALL_ROWS
        }
        q[key] = value
        setClientEngagementQuery(q)
    }

    const handleSearchClientEngagements = useCallback(() => queryClientEngagements(clientEngagementQuery), [clientEngagementQuery, queryClientEngagements])

    const handleClearClientEngagementSearch = useCallback(() => {
        const q: IClientEngagementQueryParams = {
            page: 0,
            limit: ALL_ROWS,
            opportunityIds: [],
            createdByIds: [],
            clientIds: clientId ? [clientId] : [],
        }
        setClientEngagementQuery(q)
        queryClientEngagements(q)
    }, [clientId, queryClientEngagements])

    // ---- Columns ----
    const opportunityColumns: ColumnDef<Opportunity>[] = useMemo(() => [
        {
            header: "Opportunity",
            id: "description",
            accessorFn: (row) => row.description ?? "",
            cell: ({ row }) => <a
                ref={(el) => el?.style.setProperty("text-decoration", "underline", "important")}
                onClick={() => handleClickOpportunity(row.original)}
                style={{ color: "var(--bs-table-link-color)", cursor: "pointer" }}
            >
                {row.original.description || ""}
            </a>
        },
        {
            header: "Stage",
            id: "stage",
            accessorFn: (row) => row.stage?.description,
            cell: ({ row }) => row.original.stage?.description ?? "-",
            meta: {
                getCellClassName: () => 'text-end',
                getHeaderClassName: () => 'text-end'
            }
        },
        {
            header: "Total ($)",
            accessorKey: "total",
            cell: ({ getValue }) => formatCurrency(getValue()),
            meta: {
                getCellClassName: () => 'text-end',
                getHeaderClassName: () => 'text-end'
            },
        },
        {
            header: "Close Date",
            accessorKey: "closeDate",
            meta: {
                getCellClassName: () => 'text-end',
                getHeaderClassName: () => 'text-end'
            },
            cell: ({ getValue }) => getValue() ? String(getValue()).split("T")[0] : "-"
        },
        {
            header: "Owner",
            id: "owner",
            accessorFn: (row) => row.owner ? `${row.owner.commonName ?? row.owner.firstName} ${row.owner.lastName}` : undefined,
            cell: ({ row }) => {
                const owner = row.original.owner
                if (!owner) return "-"
                return `${owner.commonName ?? owner.firstName} ${owner.lastName}`
            },
            meta: {
                getCellClassName: () => 'text-end',
                getHeaderClassName: () => 'text-end'
            }
        },
    ], [])

    const projectColumns: ColumnDef<ProjectListItem>[] = useMemo(() => [
        {
            header: "Code", accessorKey: "code", cell: ({ row }) => (
                <a
                    style={{ color: "var(--bs-table-link-color)", cursor: "pointer" }}
                    onClick={() => handleClickProject(row.original.id)}
                >
                    {row.original.code || ""}
                </a>
            )
        },
        {
            header: "Description",
            accessorKey: "description",
            meta: {
                getCellClassName: () => 'text-end',
                getHeaderClassName: () => 'text-end'
            }
        },
        {
            header: "Manager",
            id: "manager",
            accessorFn: (row) => row.approver ?? undefined,
            meta: {
                getCellClassName: () => 'text-end',
                getHeaderClassName: () => 'text-end'
            },
            cell: ({ row }) => row.original.approver ?? "-"
        },
        {
            header: "Status",
            id: "status",
            accessorFn: (row) => row.status ?? undefined,
            meta: {
                getCellClassName: () => 'text-end',
                getHeaderClassName: () => 'text-end'
            },
            cell: ({ row }) => row.original.status ?? "-"
        },
    ], [])

    const clientEngagementColumns: ColumnDef<ClientEngagement>[] = useMemo(() => [
        {
            header: "Created By",
            id: "createdBy",
            accessorFn: (row) => row.createdBy?.commonName ?? `${row.createdBy?.firstName} ${row.createdBy?.lastName}`,
            cell: ({ row }) =>
                row.original.createdBy?.commonName ?? `${row.original.createdBy?.firstName} ${row.original.createdBy?.lastName}`
        },
        {
            header: "Opportunity",
            id: "opportunity",
            accessorFn: (row) => row.opportunity?.description,
            meta: {
                getCellClassName: () => 'text-end',
                getHeaderClassName: () => 'text-end'
            },
            cell: ({ row }) => row.original.opportunity?.description ?? "Not found"
        },
        {
            header: "Date Of Next Checkin",
            id: "dateCheckIn",
            accessorFn: (row) => row.dateOfNextCheckin ? row.dateOfNextCheckin.toString() : undefined,
            meta: {
                getCellClassName: () => 'text-end',
                getHeaderClassName: () => 'text-end'
            },
            cell: ({ row }) =>
                row.original.dateOfNextCheckin ? row.original.dateOfNextCheckin.toString().split("T")[0] : "Not found"
        },
        {
            header: "Feedback",
            id: "feedback",
            accessorFn: (row) => row.notes ?? undefined,
            meta: {
                getCellClassName: () => 'text-end',
                getHeaderClassName: () => 'text-end'
            },
            cell: ({ row }) => row.original.notes ?? "Not found"
        },
    ], [])

    const opportunityTable = useDataTable({ data: opportunities, columns: opportunityColumns })
    const projectTable = useDataTable({ data: projects, columns: projectColumns })
    const clientEngagementTable = useDataTable({ data: clientEngagements, columns: clientEngagementColumns })

    const sections = [{
        title: SECTION_PROJECTS,
        content: <>
            {/* <SearchProjectsForm
                query={projectQuery}
                handleChangeQuery={handleChangeProjectQuery}
                handleSearch={handleSearchProjects}
                handleClearSearch={handleClearProjectSearch}
                isSearching={isLoadingProjects}
                hide={{ customers: true }}
                customers={projectCustomers.map((c: BAccount) => ({ value: c.id, label: c.name }))}
                branches={projectBranches.map((b: BAccount) => ({ value: b.id, label: b.name }))}
                projectManagers={projectManagers.map((emp: any) => ({
                    value: emp.employeeId,
                    label: `${emp.commonName ?? emp.firstName} ${emp.lastName}`,
                }))}
                status={projectStatus.map((s: any) => ({ value: s.id, label: s.name }))}
                discipline={subAccounts.map((sub: any) => ({ value: sub.id, label: sub.label ?? sub.description }))}
            /> */}
            {
                isLoadingProjects ? <LoadingState title="Loading Projects" /> : <DataTable table={projectTable} columns={projectColumns} />
            }
        </>
    }, {
        title: SECTION_OPPORTUNITIES,
        content: <>
            {/* <SearchOpportunitiesForm
                query={opportunityQuery}
                handleChangeQuery={handleChangeOpportunityQuery}
                handleSearch={handleSearchOpportunities}
                handleClearSearch={handleClearOpportunitySearch}
                isSearching={isLoadingOpportunities}
                hide={{ clients: true }}
                branches={opportunityBranches.map((b: BAccount) => ({ value: b.id, label: b.name }))}
                projectManagers={opportunityProjectManagers.map((emp: any) => ({
                    value: emp.employeeId,
                    label: `${emp.commonName ?? emp.firstName} ${emp.lastName}`,
                }))}
                disciplines={opportunityDiscipline.map((d: any) => ({ value: d.disciplineId, label: d.label ?? d.name }))}
                divisions={opportunityDivisions.map((div: any) => ({ value: div.divisionId, label: div.name }))}
            /> */}
            {
                isLoadingOpportunities ? <LoadingState title="Loading Opportunities" /> : <DataTable table={opportunityTable} columns={opportunityColumns} />
            }
        </>
    }, {
        title: SECTION_CLIENT_ENGAGEMENTS,
        content: <>
            {/* <SearchClientEngagement
                query={clientEngagementQuery}
                handleChangeQuery={handleChangeClientEngagementQuery}
                handleSearch={handleSearchClientEngagements}
                handleClearSearch={handleClearClientEngagementSearch}
                isSearching={isLoadingClientEngagements}
                hide={{ client: true }}
                clients={clientEngagementClients.map((c: BAccount) => ({ value: c.id, label: c.name }))}
                createdBy={clientEngagementEmployees.map((e: any) => ({
                    value: e.employeeId,
                    label: `${e.commonName ?? e.firstName} ${e.lastName}`,
                }))}
                opportunities={clientEngagementOpportunities.map((o: Opportunity) => ({ value: o.opportunityId, label: o.description }))}
            /> */}
            {
                isLoadingOpportunities ? <LoadingState title="Loading Client Engagements" /> : <DataTable table={clientEngagementTable} columns={clientEngagementColumns} />
            }
        </>
    }]

    return <ClientDetailsTemplate
        client={client}
        isLoadingClient={isLoadingClient}
        sections={sections}
        handleBack={() => props.router.navigate(-1)}
        handleOpenHelpDocument={handleOpenManual}
    />
}

export default withRouter(ClientDetailsPage)
